import { useEffect, useState } from 'react';
import { adminApi } from '../../lib/api';
import { useAdminAuth } from './AdminLayout';
import { toast } from 'sonner';
import { Users, Phone, Mail, Calendar, ShoppingBag, X, Search, ChevronRight, UserCheck } from 'lucide-react';

export default function AdminUsers() {
  const { password } = useAdminAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Modal state
  const [selectedUser, setSelectedUser] = useState(null);
  const [userOrders, setUserOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);

  const client = adminApi(password);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const res = await client.get('/admin/users');
      setUsers(res.data);
    } catch (err) {
      toast.error('Failed to load registered users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, []); // eslint-disable-line

  const handleViewProfile = async (user) => {
    setSelectedUser(user);
    setUserOrders([]);
    setLoadingOrders(true);
    try {
      const res = await client.get(`/admin/users/${user.user_id}/orders`);
      setUserOrders(res.data);
    } catch (err) {
      toast.error('Failed to load user order history');
    } finally {
      setLoadingOrders(false);
    }
  };

  const getStatusStyle = (status) => {
    switch (status) {
      case 'placed':
        return 'bg-amber-100 text-amber-800 border-amber-200';
      case 'preparing':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'out_for_delivery':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'delivered':
        return 'bg-emerald-100 text-emerald-800 border-emerald-200';
      case 'cancelled':
        return 'bg-rose-100 text-rose-800 border-rose-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const formatDate = (isoString) => {
    if (!isoString) return 'N/A';
    try {
      return new Date(isoString).toLocaleDateString('en-IN', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      });
    } catch (e) {
      return 'N/A';
    }
  };

  // Filter users based on query
  const filteredUsers = users.filter((u) => {
    const query = searchQuery.toLowerCase();
    const name = (u.name || '').toLowerCase();
    const email = (u.email || '').toLowerCase();
    const phone = (u.phone || '').toLowerCase();
    return name.includes(query) || email.includes(query) || phone.includes(query);
  });

  return (
    <div>
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="font-heading font-extrabold text-3xl text-textprimary">People</h1>
          <p className="text-textsecondary font-body text-sm">
            {users.length} registered customers with order aggregate statistics
          </p>
        </div>

        {/* Search Bar */}
        <div className="relative w-full md:w-80">
          <input
            type="text"
            placeholder="Search by name, email, or phone..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-borderbeige rounded-2xl focus:outline-none focus:ring-2 focus:ring-saffron text-sm bg-white font-body shadow-sm"
          />
          <Search className="w-4 h-4 text-textsecondary absolute left-3.5 top-1/2 -translate-y-1/2" />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-3.5 top-1/2 -translate-y-1/2 text-textsecondary hover:text-textprimary"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="flex flex-col items-center justify-center py-20">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-saffron"></div>
          <p className="text-textsecondary font-body mt-4 text-sm">Loading users data...</p>
        </div>
      ) : filteredUsers.length === 0 ? (
        <div className="bg-white rounded-3xl p-12 border border-borderbeige text-center shadow-card">
          <Users className="w-12 h-12 text-borderbeige mx-auto mb-4" />
          <h3 className="font-heading font-bold text-lg text-textprimary">No customers found</h3>
          <p className="text-textsecondary font-body text-sm mt-1">
            {searchQuery ? 'Adjust your search query and try again.' : 'Registered users will appear here.'}
          </p>
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {filteredUsers.map((u) => (
            <div
              key={u.user_id}
              onClick={() => handleViewProfile(u)}
              className="bg-white rounded-3xl p-5 border border-borderbeige shadow-card hover:shadow-warm transition-all cursor-pointer group flex flex-col justify-between"
              style={{ contentVisibility: 'auto' }}
            >
              <div>
                <div className="flex items-center gap-3 mb-4">
                  {u.picture ? (
                    <img
                      src={u.picture}
                      alt={u.name}
                      referrerPolicy="no-referrer"
                      className="w-12 h-12 rounded-2xl border border-borderbeige object-cover"
                    />
                  ) : (
                    <div className="w-12 h-12 rounded-2xl bg-gradient-offer flex items-center justify-center text-maroon font-heading font-bold text-lg">
                      {(u.name || 'G')[0].toUpperCase()}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <h3 className="font-heading font-bold text-lg text-textprimary truncate group-hover:text-maroon transition-colors flex items-center gap-1.5">
                      {u.name || 'Guest'}
                      {u.profile_complete && (
                        <UserCheck className="w-4 h-4 text-emerald-600 inline" title="Profile Complete" />
                      )}
                    </h3>
                    <p className="text-xs text-textsecondary font-body truncate">Joined {formatDate(u.created_at)}</p>
                  </div>
                </div>

                <div className="space-y-2 mb-4 text-xs font-body text-textsecondary">
                  <div className="flex items-center gap-2">
                    <Mail className="w-3.5 h-3.5 flex-shrink-0" />
                    <span className="truncate">{u.email || 'No email'}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Phone className="w-3.5 h-3.5 flex-shrink-0" />
                    <span>{u.phone || 'No phone number'}</span>
                  </div>
                </div>
              </div>

              <div className="pt-3 border-t border-dashed border-borderbeige flex items-center justify-between">
                <div className="flex gap-4">
                  <div>
                    <span className="block text-[10px] uppercase tracking-wider text-textsecondary font-heading font-semibold">Orders</span>
                    <span className="font-heading font-bold text-sm text-textprimary">{u.order_count || 0}</span>
                  </div>
                  <div>
                    <span className="block text-[10px] uppercase tracking-wider text-textsecondary font-heading font-semibold">Total Spent</span>
                    <span className="font-heading font-bold text-sm text-textprimary">₹{u.total_spent || 0}</span>
                  </div>
                </div>
                <div className="w-8 h-8 rounded-full bg-orange-50 flex items-center justify-center text-saffron group-hover:bg-saffron group-hover:text-white transition-all">
                  <ChevronRight className="w-4 h-4" />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Customer Profile & Order History Modal */}
      {selectedUser && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-xs z-50 flex items-center justify-center p-4">
          <div className="bg-[#FFFDFB] rounded-[32px] border border-borderbeige shadow-warm max-w-4xl w-full max-h-[85vh] flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-200">
            {/* Modal Header */}
            <div className="p-6 border-b border-borderbeige flex justify-between items-center bg-gradient-offer">
              <div className="flex items-center gap-4">
                {selectedUser.picture ? (
                  <img
                    src={selectedUser.picture}
                    alt={selectedUser.name}
                    className="w-14 h-14 rounded-2xl object-cover border border-borderbeige shadow-sm"
                  />
                ) : (
                  <div className="w-14 h-14 rounded-2xl bg-white flex items-center justify-center text-maroon font-heading font-bold text-xl border border-borderbeige shadow-sm">
                    {(selectedUser.name || 'G')[0].toUpperCase()}
                  </div>
                )}
                <div>
                  <h2 className="font-heading font-extrabold text-2xl text-maroon flex items-center gap-2">
                    {selectedUser.name || 'Guest'}
                    {selectedUser.profile_complete && (
                      <span className="text-xs bg-emerald-100 text-emerald-800 font-heading font-semibold px-2 py-0.5 rounded-full border border-emerald-200">
                        Profile Verified
                      </span>
                    )}
                  </h2>
                  <p className="text-xs text-textsecondary font-body mt-0.5">
                    User ID: {selectedUser.user_id} • Customer Joined {formatDate(selectedUser.created_at)}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setSelectedUser(null)}
                className="w-10 h-10 rounded-full bg-white border border-borderbeige flex items-center justify-center hover:bg-orange-50 text-textsecondary hover:text-textprimary shadow-sm transition-all"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              {/* Profile Card & Info */}
              <div className="grid md:grid-cols-3 gap-4">
                <div className="bg-white p-4 rounded-2xl border border-borderbeige shadow-sm">
                  <div className="text-[10px] uppercase tracking-wider text-textsecondary font-heading font-semibold mb-1">
                    Contact Details
                  </div>
                  <div className="space-y-2 text-sm font-body text-textprimary">
                    <div className="flex items-center gap-2">
                      <Mail className="w-4 h-4 text-textsecondary" />
                      <span className="truncate">{selectedUser.email || 'N/A'}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Phone className="w-4 h-4 text-textsecondary" />
                      <span>{selectedUser.phone || 'N/A'}</span>
                    </div>
                  </div>
                </div>

                <div className="bg-white p-4 rounded-2xl border border-borderbeige shadow-sm">
                  <div className="text-[10px] uppercase tracking-wider text-textsecondary font-heading font-semibold mb-1">
                    Notifications & Consents
                  </div>
                  <div className="space-y-1.5 text-xs font-body text-textprimary">
                    <div className="flex items-center justify-between">
                      <span className="text-textsecondary">Email Promotional:</span>
                      <span className={`font-semibold ${selectedUser.email_consent !== false ? 'text-emerald-600' : 'text-rose-500'}`}>
                        {selectedUser.email_consent !== false ? 'Opted In' : 'Opted Out'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-textsecondary">WhatsApp Notifications:</span>
                      <span className={`font-semibold ${selectedUser.whatsapp_consent !== false ? 'text-emerald-600' : 'text-rose-500'}`}>
                        {selectedUser.whatsapp_consent !== false ? 'Opted In' : 'Opted Out'}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="bg-white p-4 rounded-2xl border border-borderbeige shadow-sm flex flex-col justify-between">
                  <div className="text-[10px] uppercase tracking-wider text-textsecondary font-heading font-semibold">
                    Purchase Summary
                  </div>
                  <div className="flex justify-between items-end mt-2">
                    <div>
                      <span className="text-2xl font-heading font-extrabold text-maroon">
                        ₹{selectedUser.total_spent || 0}
                      </span>
                      <span className="text-xs text-textsecondary font-body block">Total Amount Spent</span>
                    </div>
                    <div className="text-right">
                      <div className="font-heading font-extrabold text-lg text-textprimary flex items-center justify-end gap-1">
                        <ShoppingBag className="w-4 h-4 text-saffron" />
                        {selectedUser.order_count || 0}
                      </div>
                      <span className="text-xs text-textsecondary font-body block">Total Orders</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Order History */}
              <div>
                <h3 className="font-heading font-bold text-lg text-textprimary mb-3 flex items-center gap-2">
                  <ShoppingBag className="w-5 h-5 text-maroon" /> Order History
                </h3>

                {loadingOrders ? (
                  <div className="flex flex-col items-center justify-center py-10 bg-white rounded-2xl border border-borderbeige">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-saffron"></div>
                    <p className="text-textsecondary font-body mt-2 text-xs">Loading order log...</p>
                  </div>
                ) : userOrders.length === 0 ? (
                  <div className="bg-white rounded-2xl p-8 border border-borderbeige text-center">
                    <p className="text-textsecondary font-body text-sm">This customer hasn't placed any orders yet.</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {userOrders.map((order) => (
                      <div
                        key={order.id}
                        className="bg-white rounded-2xl border border-borderbeige p-4 shadow-sm hover:border-saffron/40 transition-colors"
                      >
                        <div className="flex flex-wrap items-center justify-between gap-3 pb-3 border-b border-borderbeige mb-3">
                          <div>
                            <span className="font-heading font-bold text-sm text-textprimary">
                              Order #{order.id.slice(-6).toUpperCase()}
                            </span>
                            <span className="text-xs text-textsecondary font-body block">
                              Plural items • {formatDate(order.created_at)}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="font-heading font-extrabold text-base text-maroon">
                              ₹{order.total}
                            </span>
                            <span className={`text-[10px] font-heading font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border ${getStatusStyle(order.status)}`}>
                              {order.status}
                            </span>
                            <span className={`text-[10px] font-heading font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border ${
                              order.payment_status === 'paid' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-rose-50 text-rose-700 border-rose-200'
                            }`}>
                              {order.payment_status || 'unpaid'}
                            </span>
                          </div>
                        </div>

                        {/* Order Items */}
                        <div className="space-y-2">
                          {order.items.map((item, idx) => (
                            <div key={idx} className="flex justify-between items-center text-xs font-body text-textprimary">
                              <div className="flex items-center gap-2">
                                <span className={`inline-block w-2.5 h-2.5 border rounded-xs ${item.is_veg ? 'border-emerald-600 bg-emerald-50' : 'border-rose-600 bg-rose-50'}`} />
                                <span>
                                  {item.name} <span className="text-textsecondary">x{item.quantity}</span>
                                </span>
                              </div>
                              <span className="text-textsecondary font-semibold">₹{item.price * item.quantity}</span>
                            </div>
                          ))}
                        </div>

                        {/* Delivery Address */}
                        {order.address && (
                          <div className="mt-3 pt-3 border-t border-dashed border-borderbeige text-[11px] font-body text-textsecondary">
                            <span className="font-semibold text-textprimary block mb-0.5">Shipping Address:</span>
                            {order.address.address_line} {order.address.phone && `• Tel: ${order.address.phone}`}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="p-4 border-t border-borderbeige bg-white flex justify-end">
              <button
                onClick={() => setSelectedUser(null)}
                className="bg-saffron text-white px-6 py-2 rounded-2xl font-heading font-bold shadow-warm hover:opacity-90"
              >
                Close Profile
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
